import { useAuth } from '@eg/compartido'
import { ShieldCheck } from 'lucide-react'
import { useEffect, useId, useState, type FormEvent } from 'react'
import { es } from '../../content/es'
import { supabase } from '../../lib/supabase'

type Factor = { id: string; qr?: string; secreto?: string }
type Modo = { tipo: 'cargando' } | { tipo: 'enrolar'; factor: Factor } | { tipo: 'verificar'; factor: Factor } | { tipo: 'error' }

/**
 * Segundo paso obligatorio del portal (aal2). Si la cuenta aún no tiene un factor TOTP, lo enrola con un
 * código QR; si ya lo tiene, pide el código de 6 dígitos de la aplicación de autenticación.
 */
export function Mfa() {
  const m = es.mfa
  const { recargarNivel, cerrarSesion } = useAuth()
  const idCodigo = useId()
  const [modo, setModo] = useState<Modo>({ tipo: 'cargando' })
  const [codigo, setCodigo] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [errorCodigo, setErrorCodigo] = useState(false)

  useEffect(() => {
    if (!supabase) return
    let vivo = true
    async function preparar() {
      const { data, error } = await supabase!.auth.mfa.listFactors()
      if (!vivo) return
      if (error) return setModo({ tipo: 'error' })
      const verificado = data.totp.find((f) => f.status === 'verified')
      if (verificado) return setModo({ tipo: 'verificar', factor: { id: verificado.id } })
      // Un factor a medio enrolar (sin verificar) se descarta para poder mostrar un QR nuevo.
      for (const f of data.all.filter((x) => x.status === 'unverified')) await supabase!.auth.mfa.unenroll({ factorId: f.id })
      const nuevo = await supabase!.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Aplicación de autenticación' })
      if (!vivo) return
      if (nuevo.error || !nuevo.data) return setModo({ tipo: 'error' })
      setModo({ tipo: 'enrolar', factor: { id: nuevo.data.id, qr: nuevo.data.totp.qr_code, secreto: nuevo.data.totp.secret } })
    }
    void preparar()
    return () => {
      vivo = false
    }
  }, [])

  async function confirmar(e: FormEvent) {
    e.preventDefault()
    if (!supabase || modo.tipo === 'cargando' || modo.tipo === 'error' || enviando) return
    setEnviando(true)
    setErrorCodigo(false)
    const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId: modo.factor.id, code: codigo.replace(/\s/g, '') })
    if (error) {
      setErrorCodigo(true)
      setEnviando(false)
      return
    }
    await recargarNivel()
    setEnviando(false)
  }

  if (modo.tipo === 'cargando') {
    return (
      <div role="status" aria-live="polite">
        <p className="principal__intro">{m.cargando}</p>
        <div className="esqueleto" />
      </div>
    )
  }
  if (modo.tipo === 'error') {
    return (
      <div className="aviso-vacio aviso-vacio--error" role="alert">
        <h2>{m.errorTitulo}</h2>
        <p>{m.errorTexto}</p>
        <p className="accion">
          <button type="button" className="boton boton--secundario" onClick={() => void cerrarSesion()}>
            {es.cerrarSesion}
          </button>
        </p>
      </div>
    )
  }

  const enrolando = modo.tipo === 'enrolar'
  return (
    <div className="mfa">
      {enrolando && (
        <div className="mfa__pasos">
          <p className="prosa">{m.enrolarIntro}</p>
          {modo.factor.qr && <img className="mfa__qr" src={modo.factor.qr} width={200} height={200} alt={m.altQr} />}
          {modo.factor.secreto && (
            <p className="prosa">
              {m.secretoManual} <code className="mfa__secreto">{modo.factor.secreto}</code>
            </p>
          )}
        </div>
      )}
      {!enrolando && <p className="prosa">{m.verificarIntro}</p>}

      <form className="formulario" onSubmit={(e) => void confirmar(e)}>
        <div className="campo">
          <label htmlFor={idCodigo}>{m.codigo}</label>
          <input
            id={idCodigo}
            type="text"
            name="codigo"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9 ]{6,7}"
            maxLength={7}
            required
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            aria-invalid={errorCodigo}
            aria-describedby={errorCodigo ? `${idCodigo}-error` : undefined}
            className="campo__codigo"
          />
          {errorCodigo && (
            <p id={`${idCodigo}-error`} className="campo__error" role="alert">
              {m.codigoIncorrecto}
            </p>
          )}
        </div>
        <button type="submit" className="boton boton--primario" disabled={enviando} aria-busy={enviando}>
          <ShieldCheck className="icono" strokeWidth={1.75} aria-hidden="true" />
          {enviando ? m.verificando : enrolando ? m.activar : m.verificar}
        </button>
      </form>
    </div>
  )
}
